import { create } from "zustand";
import { apiClient } from "../api/client";
import { wsClient } from "../api/websocket";

// Room types
export interface RoomConfig {
  maxPlayers: number;
  templateId: string;
  turnTimeLimit: number;
  visibility: "public" | "private";
  allowSpectators: boolean;
  serverDice: boolean;
}

export interface RoomPlayer {
  odId: string;
  username: string;
  isHost: boolean;
  isConnected: boolean;
}

export interface Room {
  id: string;
  code: string;
  status: "waiting" | "active" | "finished" | "archived";
  config: RoomConfig;
  players: RoomPlayer[];
  hostId: string;
  gameId: string | null;
  createdAt: number;
  startedAt: number | null;
}

// Multiplayer game types
export interface MultiplayerPlayer {
  odId: string;
  username: string;
  sheet: Record<string, Record<string, number | null>>;
  totalScore: number;
  currentRoll: number[];
  holds: boolean[];
  rollCount: number;
  announcedCategory: string | null;
  turnStartedAt: number | null;
}

export interface MultiplayerGameState {
  id: string;
  roomId: string;
  templateId: string;
  status: "in_progress" | "completed";
  currentTurnPlayerId: string;
  turnOrder: string[];
  turnNumber: number;
  players: MultiplayerPlayer[];
  createdAt: number;
  updatedAt: number;
}

interface MultiplayerState {
  // Room state
  room: Room | null;
  game: MultiplayerGameState | null;
  publicRooms: Room[];

  // Connection state
  isConnected: boolean;
  isLoading: boolean;
  error: string | null;

  // Actions
  createRoom: (config?: Partial<RoomConfig>) => Promise<Room>;
  joinRoom: (code: string) => Promise<Room>;
  leaveRoom: () => Promise<void>;
  startGame: () => Promise<void>;
  kickPlayer: (odId: string) => Promise<void>;
  updateConfig: (config: Partial<RoomConfig>) => Promise<void>;
  loadPublicRooms: () => Promise<void>;

  // Game actions
  roll: (holds?: boolean[]) => void;
  announce: (category: string) => void;
  score: (columnId: string, category: string) => void;

  // Connection
  connect: () => Promise<void>;
  disconnect: () => void;
  reconnectToRoom: (roomId: string) => Promise<void>;

  // Helpers
  clearError: () => void;
  reset: () => void;
}

const API_BASE = import.meta.env.VITE_API_URL ?? "http://localhost:4000";

export const useMultiplayerStore = create<MultiplayerState>((set, get) => {
  // Set up WebSocket handlers
  const setupWsHandlers = () => {
    wsClient.on("room:joined", (msg) => {
      const payload = msg.payload as { room: Room; game?: MultiplayerGameState };
      set({ room: payload.room, game: payload.game ?? null });
    });

    wsClient.on("room:left", () => {
      set({ room: null, game: null });
    });

    wsClient.on("room:updated", (msg) => {
      const payload = msg.payload as { room: Room };
      set({ room: payload.room });
    });

    wsClient.on("player:joined", (msg) => {
      const payload = msg.payload as { room: Room };
      set({ room: payload.room });
    });

    wsClient.on("player:left", (msg) => {
      const payload = msg.payload as { room: Room };
      set({ room: payload.room });
    });

    wsClient.on("player:connected", (msg) => {
      const payload = msg.payload as { odId: string };
      set((state) => {
        if (!state.room) return state;
        return {
          room: {
            ...state.room,
            players: state.room.players.map((p) =>
              p.odId === payload.odId ? { ...p, isConnected: true } : p
            ),
          },
        };
      });
    });

    wsClient.on("player:disconnected", (msg) => {
      const payload = msg.payload as { odId: string };
      set((state) => {
        if (!state.room) return state;
        return {
          room: {
            ...state.room,
            players: state.room.players.map((p) =>
              p.odId === payload.odId ? { ...p, isConnected: false } : p
            ),
          },
        };
      });
    });

    wsClient.on("game:started", (msg) => {
      const payload = msg.payload as { game: MultiplayerGameState; room: Room };
      set({ game: payload.game, room: payload.room });
    });

    wsClient.on("game:rolled", (msg) => {
      const payload = msg.payload as { odId: string; dice: number[]; rollCount: number };
      set((state) => {
        if (!state.game) return state;
        return {
          game: {
            ...state.game,
            players: state.game.players.map((p) =>
              p.odId === payload.odId
                ? { ...p, currentRoll: payload.dice, rollCount: payload.rollCount }
                : p
            ),
          },
        };
      });
    });

    wsClient.on("game:announced", (msg) => {
      const payload = msg.payload as { odId: string; category: string };
      set((state) => {
        if (!state.game) return state;
        return {
          game: {
            ...state.game,
            players: state.game.players.map((p) =>
              p.odId === payload.odId ? { ...p, announcedCategory: payload.category } : p
            ),
          },
        };
      });
    });

    wsClient.on("game:turn_changed", (msg) => {
      const payload = msg.payload as {
        previousPlayer: string;
        currentPlayer: string;
        turnNumber: number;
        score: { odId: string; columnId: string; category: string; value: number };
      };
      set((state) => {
        if (!state.game) return state;
        return {
          game: {
            ...state.game,
            currentTurnPlayerId: payload.currentPlayer,
            turnNumber: payload.turnNumber,
            players: state.game.players.map((p) => {
              if (p.odId === payload.score.odId) {
                const newSheet = { ...p.sheet };
                if (!newSheet[payload.score.columnId]) {
                  newSheet[payload.score.columnId] = {};
                }
                newSheet[payload.score.columnId]![payload.score.category] = payload.score.value;
                return {
                  ...p,
                  sheet: newSheet,
                  totalScore: p.totalScore + payload.score.value,
                  currentRoll: [],
                  holds: [false, false, false, false, false],
                  rollCount: 0,
                  announcedCategory: null,
                  turnStartedAt: null,
                };
              }
              if (p.odId === payload.currentPlayer) {
                return { ...p, turnStartedAt: Date.now() };
              }
              return p;
            }),
          },
        };
      });
    });

    wsClient.on("game:finished", (msg) => {
      const payload = msg.payload as {
        game: MultiplayerGameState;
        winner: { odId: string; username: string; score: number };
      };
      set((state) => ({
        game: payload.game,
        room: state.room ? { ...state.room, status: "finished" } : null,
      }));
    });

    wsClient.on("error", (msg) => {
      const payload = msg.payload as { message: string };
      set({ error: payload.message });
    });
  };

  // Initialize handlers
  setupWsHandlers();

  return {
    room: null,
    game: null,
    publicRooms: [],
    isConnected: false,
    isLoading: false,
    error: null,

    createRoom: async (config) => {
      set({ isLoading: true, error: null });
      try {
        const response = await fetch(`${API_BASE}/api/rooms`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${apiClient.getToken()}`,
          },
          body: JSON.stringify({ config }),
        });

        if (!response.ok) {
          const data = await response.json();
          throw new Error(data.error ?? "Failed to create room");
        }

        const { room } = await response.json();
        set({ room, isLoading: false });

        // Connect to WebSocket and join room
        await get().connect();
        wsClient.send("room:connect", { roomId: room.id });

        return room;
      } catch (error) {
        set({
          error: error instanceof Error ? error.message : "Failed to create room",
          isLoading: false,
        });
        throw error;
      }
    },

    joinRoom: async (code) => {
      set({ isLoading: true, error: null });
      try {
        const response = await fetch(`${API_BASE}/api/rooms/join`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${apiClient.getToken()}`,
          },
          body: JSON.stringify({ code }),
        });

        if (!response.ok) {
          const data = await response.json();
          throw new Error(data.error ?? "Failed to join room");
        }

        const { room } = await response.json();
        set({ room, isLoading: false });

        // Connect to WebSocket and join room
        await get().connect();
        wsClient.send("room:connect", { roomId: room.id });

        return room;
      } catch (error) {
        set({
          error: error instanceof Error ? error.message : "Failed to join room",
          isLoading: false,
        });
        throw error;
      }
    },

    leaveRoom: async () => {
      const { room } = get();
      if (!room) return;

      try {
        await fetch(`${API_BASE}/api/rooms/${room.id}/leave`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${apiClient.getToken()}`,
          },
        });
      } catch {
        // Ignore errors when leaving
      }

      wsClient.send("room:leave", { roomId: room.id });
      set({ room: null, game: null });
    },

    startGame: async () => {
      const { room } = get();
      if (!room) return;

      set({ isLoading: true, error: null });
      try {
        const response = await fetch(`${API_BASE}/api/rooms/${room.id}/action`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${apiClient.getToken()}`,
          },
          body: JSON.stringify({ action: "start" }),
        });

        if (!response.ok) {
          const data = await response.json();
          throw new Error(data.error ?? "Failed to start game");
        }

        set({ isLoading: false });
      } catch (error) {
        set({
          error: error instanceof Error ? error.message : "Failed to start game",
          isLoading: false,
        });
        throw error;
      }
    },

    kickPlayer: async (odId) => {
      const { room } = get();
      if (!room) return;

      try {
        await fetch(`${API_BASE}/api/rooms/${room.id}/action`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${apiClient.getToken()}`,
          },
          body: JSON.stringify({ action: "kick", odId }),
        });
      } catch (error) {
        set({ error: error instanceof Error ? error.message : "Failed to kick player" });
      }
    },

    updateConfig: async (config) => {
      const { room } = get();
      if (!room) return;

      try {
        await fetch(`${API_BASE}/api/rooms/${room.id}/action`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${apiClient.getToken()}`,
          },
          body: JSON.stringify({ action: "update_config", config }),
        });
      } catch (error) {
        set({ error: error instanceof Error ? error.message : "Failed to update config" });
      }
    },

    loadPublicRooms: async () => {
      try {
        const response = await fetch(`${API_BASE}/api/rooms/public`, {
          headers: {
            Authorization: `Bearer ${apiClient.getToken()}`,
          },
        });

        if (!response.ok) {
          throw new Error("Failed to load rooms");
        }

        const { rooms } = await response.json();
        set({ publicRooms: rooms });
      } catch (error) {
        set({ error: error instanceof Error ? error.message : "Failed to load rooms" });
      }
    },

    roll: (holds) => {
      const { room, game } = get();
      if (!room || !game) return;

      wsClient.send("game:roll", { roomId: room.id, holds });
    },

    announce: (category) => {
      const { room, game } = get();
      if (!room || !game) return;

      wsClient.send("game:announce", { roomId: room.id, category });
    },

    score: (columnId, category) => {
      const { room, game } = get();
      if (!room || !game) return;

      wsClient.send("game:score", { roomId: room.id, columnId, category });
    },

    connect: async () => {
      try {
        await wsClient.connect();
        set({ isConnected: true });
      } catch (error) {
        set({
          isConnected: false,
          error: error instanceof Error ? error.message : "Connection failed",
        });
        throw error;
      }
    },

    disconnect: () => {
      wsClient.disconnect();
      set({ isConnected: false });
    },

    reconnectToRoom: async (roomId) => {
      await get().connect();
      wsClient.send("room:connect", { roomId });
    },

    clearError: () => set({ error: null }),

    reset: () => {
      wsClient.disconnect();
      set({
        room: null,
        game: null,
        publicRooms: [],
        isConnected: false,
        isLoading: false,
        error: null,
      });
    },
  };
});
